import React, { useState, useEffect, useCallback } from "react";
import { useAuthContext } from "../../context/AuthContext";
import { useToast } from "../../components/Toast/ToastContext";
import { ROLES } from "../../constants";
import BottomSheet from "../../components/BottomSheet/BottomSheet";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { sanitizeInput, validateTen } from "../../utils/validation";

const MAU_NGANH = [
  '#E91E63','#C2185B','#F06292','#2E7D32','#4CAF50','#1B5E20',
  '#1565C0','#2196F3','#0D47A1','#F57F17','#FFB300','#FF8F00',
  '#6A1B9A','#9C27B0','#4A148C','#BF360C','#F44336','#D32F2F',
  '#00695C','#00897B','#004D40','#37474F','#546E7A','#795548',
  '#FF7043','#00BCD4','#1A237E','#880E4F',
];

const generateMa = (prefix, existingList, fieldName) => {
  let num = 1;
  const used = existingList.map(item => item[fieldName]);
  while (used.includes(`${prefix}${String(num).padStart(2, '0')}`)) { num++; }
  return `${prefix}${String(num).padStart(2, '0')}`;
};

const CaiDat = () => {
  const { user, hasRole } = useAuthContext();
  const { success, error } = useToast();
  const isAdmin = hasRole(ROLES.ADMIN);
  const [activeTab, setActiveTab] = useState("nganh");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nganhList, setNganhList] = useState([]);
  const [lopHocList, setLopHocList] = useState([]);
  const [giaoHoList, setGiaoHoList] = useState([]);
  const [form, setForm] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [nganhSnap, lopSnap, giaoHoSnap] = await Promise.all([
        getDocs(collection(db, "nganh")),
        getDocs(collection(db, "lopHoc")),
        getDocs(collection(db, "giaoHo"))
      ]);
      setNganhList(nganhSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLopHocList(lopSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setGiaoHoList(giaoHoSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openAddForm = (type) => {
    setEditingItem(null);
    if (type === "nganh") setForm({ tenNganh: "", maNganh: generateMa("NG", nganhList, "maNganh"), doTuoi: "", mauSac: "#E91E63" });
    else if (type === "lopHoc") setForm({ tenLop: "", maLop: generateMa("LH", lopHocList, "maLop"), nganhId: "" });
    else if (type === "giaoHo") setForm({ tenGiaoHo: "", diaChi: "" });
    setShowForm(true);
  };

  const openEditForm = (type, item) => {
    setEditingItem(item);
    if (type === "nganh") setForm({ tenNganh: item.tenNganh || "", maNganh: item.maNganh || "", doTuoi: item.doTuoi || "", mauSac: item.mauSac || "#E91E63" });
    else if (type === "lopHoc") setForm({ tenLop: item.tenLop || "", maLop: item.maLop || "", nganhId: item.nganhId || "" });
    else if (type === "giaoHo") setForm({ tenGiaoHo: item.tenGiaoHo || "", diaChi: item.diaChi || "" });
    setShowForm(true);
  };

  const handleSave = async () => {
    const ten = form.tenNganh || form.tenLop || form.tenGiaoHo;
    if (!validateTen(ten)) { error("Tên không được để trống!"); return; }
    setSaving(true);
    try {
      const cleanData = { ...form };
      if (cleanData.tenNganh) cleanData.tenNganh = sanitizeInput(cleanData.tenNganh);
      if (cleanData.tenLop) cleanData.tenLop = sanitizeInput(cleanData.tenLop);
      if (cleanData.tenGiaoHo) cleanData.tenGiaoHo = sanitizeInput(cleanData.tenGiaoHo);
      if (cleanData.diaChi) cleanData.diaChi = sanitizeInput(cleanData.diaChi);

      const collectionName = activeTab === "nganh" ? "nganh" : activeTab === "lopHoc" ? "lopHoc" : "giaoHo";
      if (editingItem) { await updateDoc(doc(db, collectionName, editingItem.id), cleanData); success("Cập nhật thành công!"); }
      else { await addDoc(collection(db, collectionName), cleanData); success("Thêm mới thành công!"); }
      setShowForm(false); loadData();
    } catch (err) { error("Lỗi: " + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (type, item) => {
    const ten = type === "nganh" ? item.tenNganh : type === "lopHoc" ? item.tenLop : item.tenGiaoHo;
    if (!window.confirm(`Xóa "${ten}"?`)) return;
    try {
      const collectionName = type === "nganh" ? "nganh" : type === "lopHoc" ? "lopHoc" : "giaoHo";
      await deleteDoc(doc(db, collectionName, item.id));
      success("Đã xóa!"); loadData();
    } catch (err) { error("Lỗi: " + err.message); }
  };

  if (!isAdmin) return (
    <div className="flex items-center justify-center h-64 text-on-surface-variant">
      <div className="text-center">
        <span className="material-symbols-outlined text-[48px]">lock</span>
        <p className="mt-2">⛔ Không có quyền truy cập</p>
      </div>
    </div>
  );

  const getTenNganh = (id) => nganhList.find(n => n.maNganh === id)?.tenNganh || id;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span>
    </div>
  );

  return (
    <div className={`space-y-4 pb-20 md:pb-6 ${showForm ? 'overflow-hidden max-h-[calc(100vh-64px)]' : ''}`}>
      <h2 className="text-[24px] md:text-[28px] font-bold text-primary">⚙️ Cài đặt hệ thống</h2>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-container rounded-xl p-1">
        {[
          { key: 'nganh', icon: 'church', label: 'Ngành' },
          { key: 'lopHoc', icon: 'school', label: 'Lớp học' },
          { key: 'giaoHo', icon: 'location_city', label: 'Giáo họ' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-all
              ${activeTab === tab.key ? 'bg-primary text-on-primary shadow-sm' : 'text-on-surface-variant hover:bg-surface'}`}>
            <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <span className="text-caption text-on-surface-variant">{activeTab === "nganh" ? nganhList.length : activeTab === "lopHoc" ? lopHocList.length : giaoHoList.length} mục</span>
        <button onClick={() => openAddForm(activeTab)} className="px-4 py-2 bg-primary text-on-primary rounded-full text-body-md font-semibold flex items-center gap-1.5 hover:bg-primary/90 transition-colors">
          <span className="material-symbols-outlined text-[18px]">add</span>Thêm mới
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {activeTab === "nganh" && nganhList.map(item => (
          <div key={item.id} className="bg-surface rounded-xl p-4 shadow-sm border border-outline-variant flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex-shrink-0" style={{background: item.mauSac || '#1a237e'}} />
              <div>
                <p className="text-body-md font-semibold">{item.tenNganh}</p>
                <p className="text-caption text-on-surface-variant">Mã: {item.maNganh} · {item.doTuoi || "—"}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEditForm("nganh", item)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-[18px] text-on-surface-variant">edit</span>
              </button>
              <button onClick={() => handleDelete("nganh", item)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-error-container transition-colors">
                <span className="material-symbols-outlined text-[18px] text-error">delete</span>
              </button>
            </div>
          </div>
        ))}
        {activeTab === "lopHoc" && lopHocList.map(item => (
          <div key={item.id} className="bg-surface rounded-xl p-4 shadow-sm border border-outline-variant flex items-center justify-between">
            <div>
              <p className="text-body-md font-semibold">{item.tenLop}</p>
              <p className="text-caption text-on-surface-variant">Mã: {item.maLop} · Ngành: {getTenNganh(item.nganhId)}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEditForm("lopHoc", item)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-[18px] text-on-surface-variant">edit</span>
              </button>
              <button onClick={() => handleDelete("lopHoc", item)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-error-container transition-colors">
                <span className="material-symbols-outlined text-[18px] text-error">delete</span>
              </button>
            </div>
          </div>
        ))}
        {activeTab === "giaoHo" && giaoHoList.map(item => (
          <div key={item.id} className="bg-surface rounded-xl p-4 shadow-sm border border-outline-variant flex items-center justify-between">
            <div>
              <p className="text-body-md font-semibold">{item.tenGiaoHo}</p>
              <p className="text-caption text-on-surface-variant">{item.diaChi || "—"}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEditForm("giaoHo", item)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-[18px] text-on-surface-variant">edit</span>
              </button>
              <button onClick={() => handleDelete("giaoHo", item)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-error-container transition-colors">
                <span className="material-symbols-outlined text-[18px] text-error">delete</span>
              </button>
            </div>
          </div>
        ))}
        {((activeTab === "nganh" && nganhList.length === 0) || (activeTab === "lopHoc" && lopHocList.length === 0) || (activeTab === "giaoHo" && giaoHoList.length === 0)) && (
          <div className="text-center py-12 text-on-surface-variant">
            <span className="material-symbols-outlined text-[48px]">inbox</span>
            <p className="mt-2">Chưa có dữ liệu</p>
          </div>
        )}
      </div>

      {/* BottomSheet Form */}
      <BottomSheet isOpen={showForm} onClose={() => setShowForm(false)} title={editingItem ? "✏️ Chỉnh sửa" : "➕ Thêm mới"}>
        <div className="space-y-3">
          {activeTab === "nganh" && (
            <>
              <div>
                <label className="text-caption font-bold text-on-surface-variant block mb-1">Tên ngành *</label>
                <input value={form.tenNganh || ""} onChange={e => setForm(p => ({ ...p, tenNganh: e.target.value }))} placeholder="Chiên Con"
                  className="w-full px-3 py-2.5 bg-surface-container-low border border-outline-variant rounded-xl text-body-md outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-caption font-bold text-on-surface-variant block mb-1">Mã ngành</label>
                  <input value={form.maNganh || ""} disabled
                    className="w-full px-3 py-2.5 bg-gray-100 border border-outline-variant rounded-xl text-body-md text-on-surface-variant" />
                </div>
                <div>
                  <label className="text-caption font-bold text-on-surface-variant block mb-1">Độ tuổi</label>
                  <input value={form.doTuoi || ""} onChange={e => setForm(p => ({ ...p, doTuoi: e.target.value }))} placeholder="6-8 tuổi"
                    className="w-full px-3 py-2.5 bg-surface-container-low border border-outline-variant rounded-xl text-body-md outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>
              <div>
                <label className="text-caption font-bold text-on-surface-variant block mb-1">Màu sắc</label>
                <div className="flex flex-wrap gap-1.5">
                  {MAU_NGANH.map(mau => (
                    <button key={mau} onClick={() => setForm(p => ({ ...p, mauSac: mau }))}
                      className={`w-8 h-8 rounded-full transition-all ${form.mauSac === mau ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'}`}
                      style={{background: mau}} />
                  ))}
                </div>
              </div>
            </>
          )}
          {activeTab === "lopHoc" && (
            <>
              <div>
                <label className="text-caption font-bold text-on-surface-variant block mb-1">Tên lớp *</label>
                <input value={form.tenLop || ""} onChange={e => setForm(p => ({ ...p, tenLop: e.target.value }))} placeholder="Chiên Con 1A"
                  className="w-full px-3 py-2.5 bg-surface-container-low border border-outline-variant rounded-xl text-body-md outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-caption font-bold text-on-surface-variant block mb-1">Mã lớp</label>
                  <input value={form.maLop || ""} disabled
                    className="w-full px-3 py-2.5 bg-gray-100 border border-outline-variant rounded-xl text-body-md text-on-surface-variant" />
                </div>
                <div>
                  <label className="text-caption font-bold text-on-surface-variant block mb-1">Ngành *</label>
                  <select value={form.nganhId || ""} onChange={e => setForm(p => ({ ...p, nganhId: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-surface-container-low border border-outline-variant rounded-xl text-body-md outline-none focus:ring-2 focus:ring-primary">
                    <option value="">-- Chọn --</option>
                    {nganhList.map(n => <option key={n.id} value={n.maNganh}>{n.tenNganh}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}
          {activeTab === "giaoHo" && (
            <>
              <div>
                <label className="text-caption font-bold text-on-surface-variant block mb-1">Tên giáo họ *</label>
                <input value={form.tenGiaoHo || ""} onChange={e => setForm(p => ({ ...p, tenGiaoHo: e.target.value }))} placeholder="Vân Tập"
                  className="w-full px-3 py-2.5 bg-surface-container-low border border-outline-variant rounded-xl text-body-md outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="text-caption font-bold text-on-surface-variant block mb-1">Địa chỉ</label>
                <input value={form.diaChi || ""} onChange={e => setForm(p => ({ ...p, diaChi: e.target.value }))} placeholder="Thôn..."
                  className="w-full px-3 py-2.5 bg-surface-container-low border border-outline-variant rounded-xl text-body-md outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </>
          )}
          <div className="flex gap-2 pt-2">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 bg-surface border border-outline-variant rounded-xl text-body-md font-semibold hover:bg-surface-container transition-colors">Hủy</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-primary text-on-primary rounded-xl text-body-md font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50">
              {saving ? <span className="material-symbols-outlined animate-spin text-[18px]">sync</span> : editingItem ? 'Cập nhật' : 'Thêm mới'}
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default CaiDat;